import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ToolPageData } from '@/lib/directory'

import Link from 'next/link'
import {
    ArrowRight,
    ArrowUpRight,
    Check,
    ChevronRight,
    Ghost,
    Minus
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ToolLogo } from '@/components/directory/tool-logo'
import { Badge, ScoreBadge, ToolBadges } from '@/components/directory/tool-badges'
import { RankedToolRow } from '@/components/directory/ranked-tool-row'
import { ComparisonCard } from '@/components/directory/comparison-card'
import { getToolPage } from '@/lib/directory'

interface ToolPageProps {
    data: ToolPageData
}

const sectionTitle = 'font-heading text-2xl font-bold tracking-tight text-foreground'

const ToolPage: FC<ToolPageProps> = ({ data }): ReactNode => {
    const { tool, replacement, categories, useCases, comparisons, alternatives } = data
    const ranked = categories.filter(category => category.position !== null)

    return (
        <Layout>
            <Seo
                title={tool.name}
                description={tool.tagline ?? `${tool.name}: score, free plan, pros, cons and the best alternatives.`}
            />

            <section id='tool-header' className='relative overflow-hidden border-b border-border/70'>
                <div className='bg-grid pointer-events-none absolute inset-0' aria-hidden='true' />
                <div className='hero-glow pointer-events-none absolute inset-0' aria-hidden='true' />

                <div className='relative mx-auto max-w-7xl px-4 pb-12 pt-8 sm:px-6 lg:px-8'>
                    <nav aria-label='Breadcrumb'>
                        <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                            <li><Link href='/' className='hover:text-foreground'>Home</Link></li>
                            <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                            <li><Link href='/tools' className='hover:text-foreground'>Directory</Link></li>
                            <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                            <li aria-current='page' className='text-foreground'>
                                <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                            </li>
                        </ol>
                    </nav>

                    <div className='mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                        <div className='flex items-start gap-5'>
                            <ToolLogo tool={tool} size='lg' className={tool.discontinued ? 'opacity-60 grayscale' : undefined} />

                            <div className='min-w-0'>
                                <div className='flex flex-wrap items-center gap-3'>
                                    <h1 className='font-heading text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl'>
                                        <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                                    </h1>
                                    <ToolBadges tool={tool} />
                                </div>

                                {tool.maker && (
                                    <p className='mt-1 text-sm text-muted-foreground'>
                                        by <span data-mdlfy-cms={`tools:${tool.id}:maker`}>{tool.maker}</span>
                                    </p>
                                )}

                                {tool.tagline && (
                                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-foreground/85' data-mdlfy-cms={`tools:${tool.id}:tagline`}>
                                        {tool.tagline}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='flex shrink-0 items-center gap-4'>
                            {tool.score !== null && (
                                <div className='text-center'>
                                    <ScoreBadge score={tool.score} size='lg' />
                                    <p className='mt-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground'>Our score</p>
                                </div>
                            )}

                            {tool.website_url && !tool.discontinued && (
                                <a
                                    href={tool.website_url}
                                    target='_blank'
                                    rel='noopener noreferrer nofollow'
                                    className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                                >
                                    Visit website
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                    <span className='sr-only'>(opens in a new tab)</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <div className='mx-auto grid max-w-7xl gap-10 px-4 pt-10 sm:px-6 lg:grid-cols-[1fr_340px] lg:px-8'>
                <div className='min-w-0 space-y-12'>
                    {tool.discontinued && (
                        <section id='what-happened' aria-labelledby='what-happened-heading' className='rounded-2xl border border-rose-400/30 bg-rose-400/5 p-6'>
                            <div className='flex flex-wrap items-center gap-3'>
                                <Ghost className='size-5 text-rose-300' aria-hidden='true' />
                                <h2 id='what-happened-heading' className='font-heading text-xl font-semibold text-foreground'>
                                    What happened
                                </h2>
                                {tool.fate && (
                                    <Badge tone='dead'>
                                        <span data-mdlfy-cms={`tools:${tool.id}:fate`}>{tool.fate}</span>
                                    </Badge>
                                )}
                                {tool.ended_label && (
                                    <span className='font-mono text-xs text-muted-foreground'>{tool.ended_label}</span>
                                )}
                            </div>

                            {tool.fate_story && (
                                <p className='mt-3 leading-relaxed text-foreground/85' data-mdlfy-cms={`tools:${tool.id}:fate_story`}>
                                    {tool.fate_story}
                                </p>
                            )}

                            {replacement && (
                                <div className='relative mt-5 flex items-center gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40'>
                                    <ToolLogo tool={replacement} size='md' />
                                    <div className='min-w-0 flex-1'>
                                        <p className='font-mono text-[11px] uppercase tracking-wider text-primary'>Use instead</p>
                                        <Link
                                            href={`/tools/${replacement.slug}`}
                                            className='font-heading text-lg font-semibold text-foreground after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-xl focus-visible:after:outline-2 focus-visible:after:outline-primary'
                                        >
                                            <span data-mdlfy-cms={`tools:${replacement.id}:name`}>{replacement.name}</span>
                                        </Link>
                                        {replacement.tagline && (
                                            <p className='mt-0.5 truncate text-sm text-muted-foreground' data-mdlfy-cms={`tools:${replacement.id}:tagline`}>
                                                {replacement.tagline}
                                            </p>
                                        )}
                                    </div>
                                    <ArrowRight className='size-5 shrink-0 text-muted-foreground' aria-hidden='true' />
                                </div>
                            )}
                        </section>
                    )}

                    {tool.description && (
                        <section id='about' aria-labelledby='about-heading'>
                            <h2 id='about-heading' className={sectionTitle}>
                                About <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                            </h2>
                            <p className='mt-4 text-lg leading-relaxed text-foreground/85' data-mdlfy-cms={`tools:${tool.id}:description`}>
                                {tool.description}
                            </p>
                        </section>
                    )}

                    {(tool.pros.length > 0 || tool.cons.length > 0) && (
                        <section id='pros-and-cons' aria-labelledby='pros-heading' className='grid gap-4 sm:grid-cols-2'>
                            <h2 id='pros-heading' className='sr-only'>Pros and cons</h2>

                            <div className='rounded-2xl border border-border bg-card p-6'>
                                <h3 className='font-heading text-lg font-semibold text-foreground'>What we like</h3>
                                <ul className='mt-4 space-y-3'>
                                    {tool.pros.map(item => (
                                        <li key={item} className='flex gap-3 text-sm leading-relaxed text-foreground/85'>
                                            <Check className='mt-0.5 size-4 shrink-0 text-primary' aria-hidden='true' />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className='rounded-2xl border border-border bg-card p-6'>
                                <h3 className='font-heading text-lg font-semibold text-foreground'>Watch out for</h3>
                                <ul className='mt-4 space-y-3'>
                                    {tool.cons.map(item => (
                                        <li key={item} className='flex gap-3 text-sm leading-relaxed text-foreground/85'>
                                            <Minus className='mt-0.5 size-4 shrink-0 text-rose-300' aria-hidden='true' />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                    )}

                    {comparisons.length > 0 && (
                        <section id='matchups' aria-labelledby='matchups-heading'>
                            <h2 id='matchups-heading' className={sectionTitle}>Matchups</h2>
                            <div className='mt-5 grid gap-4 sm:grid-cols-2'>
                                {comparisons.map(comparison => (
                                    <ComparisonCard key={comparison.id} comparison={comparison} />
                                ))}
                            </div>
                        </section>
                    )}

                    <section id='alternatives' aria-labelledby='alternatives-heading'>
                        <div className='flex flex-wrap items-end justify-between gap-3'>
                            <h2 id='alternatives-heading' className={sectionTitle}>
                                Top alternatives
                            </h2>
                            <Link
                                href={`/alternatives/${tool.slug}`}
                                className='inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 focus-visible:outline-2 focus-visible:outline-primary'
                            >
                                See all alternatives
                                <ArrowRight className='size-4' aria-hidden='true' />
                            </Link>
                        </div>

                        {alternatives.length > 0
                            ? (
                                <ol className='mt-5 space-y-1 rounded-2xl border border-border bg-card p-3'>
                                    {alternatives.map((alternative, index) => (
                                        <RankedToolRow key={alternative.id} tool={alternative} rank={index + 1} />
                                    ))}
                                </ol>
                            )
                            : (
                                <p className='mt-5 rounded-2xl border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground'>
                                    No alternatives listed yet.
                                </p>
                            )}
                    </section>
                </div>

                <aside className='space-y-5 lg:sticky lg:top-24 lg:self-start'>
                    <section aria-labelledby='facts-heading' className='rounded-2xl border border-border bg-card p-5'>
                        <h2 id='facts-heading' className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                            Quick facts
                        </h2>

                        <dl className='mt-4 divide-y divide-border text-sm'>
                            <div className='flex justify-between gap-4 py-2.5'>
                                <dt className='text-muted-foreground'>Score</dt>
                                <dd className='font-mono font-semibold text-foreground'>{tool.score !== null ? tool.score.toFixed(1) : 'Not scored'}</dd>
                            </div>
                            <div className='flex justify-between gap-4 py-2.5'>
                                <dt className='text-muted-foreground'>Free plan</dt>
                                <dd className='font-semibold text-foreground'>{tool.is_free ? 'Yes' : 'No'}</dd>
                            </div>
                            <div className='flex justify-between gap-4 py-2.5'>
                                <dt className='text-muted-foreground'>Open source</dt>
                                <dd className='font-semibold text-foreground'>{tool.is_open_source ? 'Yes' : 'No'}</dd>
                            </div>
                            {tool.maker && (
                                <div className='flex justify-between gap-4 py-2.5'>
                                    <dt className='text-muted-foreground'>Made by</dt>
                                    <dd className='text-right font-semibold text-foreground' data-mdlfy-cms={`tools:${tool.id}:maker`}>{tool.maker}</dd>
                                </div>
                            )}
                            {tool.best_for && (
                                <div className='py-2.5'>
                                    <dt className='text-muted-foreground'>Best for</dt>
                                    <dd className='mt-1 font-semibold text-foreground' data-mdlfy-cms={`tools:${tool.id}:best_for`}>{tool.best_for}</dd>
                                </div>
                            )}
                            {tool.pricing && (
                                <div className='py-2.5'>
                                    <dt className='text-muted-foreground'>Pricing</dt>
                                    <dd className='mt-1 text-foreground' data-mdlfy-cms={`tools:${tool.id}:pricing`}>{tool.pricing}</dd>
                                </div>
                            )}
                        </dl>
                    </section>

                    {ranked.length > 0 && (
                        <section aria-labelledby='rankings-heading' className='rounded-2xl border border-border bg-card p-5'>
                            <h2 id='rankings-heading' className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                                Rankings
                            </h2>

                            <ul className='mt-3 space-y-1'>
                                {ranked.map(category => (
                                    <li key={category.id}>
                                        <Link
                                            href={`/category/${category.slug}`}
                                            className='flex items-center gap-3 rounded-xl px-2 py-2 text-sm transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-primary'
                                        >
                                            <span className='inline-flex min-w-10 justify-center rounded-lg bg-primary/10 px-2 py-1 font-mono text-sm font-semibold text-primary'>
                                                #{category.position}
                                            </span>
                                            <span className='text-foreground'>
                                                in <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {useCases.length > 0 && (
                        <section aria-labelledby='use-cases-heading' className='rounded-2xl border border-border bg-card p-5'>
                            <h2 id='use-cases-heading' className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                                Good for
                            </h2>

                            <ul className='mt-3 flex flex-wrap gap-2'>
                                {useCases.map(useCase => (
                                    <li key={useCase.id}>
                                        <Link
                                            href={`/use-cases/${useCase.slug}`}
                                            className='inline-flex rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary'
                                        >
                                            <span data-mdlfy-cms={`use_cases:${useCase.id}:name`}>{useCase.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </aside>
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps<ToolPageProps, { slug: string }> = async ({ params }) => {
    const data = await getToolPage(params?.slug ?? '')

    if (!data) return { notFound: true, revalidate: 60 }

    return { props: { data }, revalidate: 60 }
}

export default ToolPage
